import type { ErpEnvironment } from '@/shared/erp/erpEnvironment';
import { adminApiClient } from './adminApiClient';
import { createCrudService } from './createCrudService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The environment an ERP registry points to.
 *
 * Mirrors the `ErpEnvironment` enum exposed by the origami API. Defined once in
 * `@/shared/erp/erpEnvironment` (claimflow's non-production banner reads the
 * same vocabulary) and re-exported here so admin callers keep importing it from
 * this service. Display labels and badge variants live in
 * `../configs/erpEnvironment`.
 */
export type { ErpEnvironment };

export interface ErpRegistry {
  id: string;
  slug: string;
  name: string;
  base_url: string;
  environment: ErpEnvironment | null;
  auth_type: 'api_key' | 'oauth2_client' | 'basic' | 'bearer' | 'none' | 'login_bearer';
  auth_config: Record<string, unknown> | null;
  default_headers: Record<string, string> | null;
  timeout_ms: number;
  retry_policy: Record<string, unknown> | null;
  is_active: boolean;
  is_main: boolean;
  endpoints_count: number;
  error_mappings_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateErpRegistryPayload {
  slug: string;
  name: string;
  base_url: string;
  /** Omit to leave unset; `null` clears it. */
  environment?: ErpEnvironment | null;
  auth_type: ErpRegistry['auth_type'];
  auth_config: Record<string, unknown>;
  default_headers?: Record<string, string> | null;
  timeout_ms?: number;
  retry_policy?: Record<string, unknown> | null;
  is_active?: boolean;
  is_main?: boolean;
}

export interface UpdateErpRegistryPayload {
  slug?: string;
  name?: string;
  base_url?: string;
  /** Omit to leave unchanged; `null` clears it. */
  environment?: ErpEnvironment | null;
  auth_type?: ErpRegistry['auth_type'];
  auth_config?: Record<string, unknown>;
  default_headers?: Record<string, string> | null;
  timeout_ms?: number;
  retry_policy?: Record<string, unknown> | null;
  is_active?: boolean;
  is_main?: boolean;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  response_time_ms: number;
}

export interface RateLimitStatus {
  enabled: boolean;
  limit: number;
  remaining: number;
  window_seconds: number;
  resets_at: number;
  seconds_until_reset: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const crud = createCrudService<
  ErpRegistry,
  CreateErpRegistryPayload,
  UpdateErpRegistryPayload
>('/admin/erp/registries');

export const erpRegistriesService = {
  ...crud,

  async testConnection(id: string): Promise<TestConnectionResponse> {
    const response = await adminApiClient.post<TestConnectionResponse>(
      `/admin/erp/registries/${id}/test-connection`,
    );
    return response.data;
  },

  async getRateLimitStatus(id: string): Promise<RateLimitStatus> {
    const response = await adminApiClient.get<RateLimitStatus>(
      `/admin/erp/registries/${id}/rate-limit-status`,
    );
    return response.data;
  },
};
